import type { TerminalGraphSearchDocument } from "./terminal-graph-data"

export type { TerminalGraphSearchDocument }

/**
 * These limits are intentionally named and centralized. The reference's exact
 * search cap and click/drag threshold remain OPEN in the reference contract;
 * they are implementation seams, not source-verified claims.
 */
const TERMINAL_GRAPH_QUERY_MAX_LENGTH = 120
const TERMINAL_GRAPH_DRAG_MOVEMENT_THRESHOLD_PX = 6
const TERMINAL_GRAPH_DEFAULT_COLOR_MODE = "monochrome" as const

const TERMINAL_GRAPH_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export type TerminalGraphColorMode = "monochrome" | "spectrum"
export type TerminalGraphTheme = "light" | "dark"

type TerminalGraphPhase =
  | "reserved"
  | "loading"
  | "revealing"
  | "idle"
  | "hovered"
  | "dragging"
  | "searching"
  | "selected"
  | "fallback"

export type TerminalGraphSelectionSource =
  | "pointer"
  | "keyboard"
  | "search"
  | "route"
  | "related"
  | "semantic"

type TerminalGraphFocusTarget = "canvas" | "search" | "selected-term"

type TerminalGraphFailureKind =
  | "data"
  | "webgl"
  | "context-loss"
  | "load"
  | "runtime"
  | "unknown"

type TerminalGraphFailure = {
  kind: TerminalGraphFailureKind
  message: string
}

type TerminalGraphSearchState = {
  isOpen: boolean
  query: string
  resultSlugs: string[]
  activeResultIndex: number
}

type TerminalGraphPanelStatus = "closed" | "entering" | "open"

type TerminalGraphPanelState = {
  status: TerminalGraphPanelStatus
}

type TerminalGraphDragState = {
  isActive: boolean
  pointerId: number | null
  startX: number | null
  startY: number | null
  lastX: number | null
  lastY: number | null
  velocityX: number
  velocityY: number
  hasMoved: boolean
  returnPhase: "idle" | "selected"
}

export type TerminalGraphState = {
  phase: TerminalGraphPhase
  selectedSlug: string | null
  selectedSource: TerminalGraphSelectionSource | null
  pendingSelectedSlug: string | null
  hoveredSlug: string | null
  drag: TerminalGraphDragState
  search: TerminalGraphSearchState
  panel: TerminalGraphPanelState
  colorMode: TerminalGraphColorMode
  isSoundEnabled: boolean
  isReducedMotion: boolean
  failure: TerminalGraphFailure | null
  focusRestore: TerminalGraphFocusTarget | null
}

export type TerminalGraphRouteState = {
  q?: string
  term?: string
}

export type CreateTerminalGraphStateOptions = {
  isReducedMotion?: boolean
  colorMode?: TerminalGraphColorMode
  isSoundEnabled?: boolean
}

export type TerminalGraphReducerOptions = {
  validSlugs?: ReadonlySet<string>
}

export type TerminalGraphAction =
  | { type: "section-visible" }
  | { type: "engine-ready" }
  | { type: "reveal-complete" }
  | { type: "open-search"; query?: string }
  | { type: "close-search"; shouldClearQuery?: boolean }
  | {
      type: "set-query"
      query: string
      resultSlugs?: readonly string[]
    }
  | { type: "clear-query" }
  | {
      type: "set-search-results"
      resultSlugs: readonly string[]
    }
  | { type: "move-search-result"; delta: number }
  | { type: "commit-search" }
  | {
      type: "select-term"
      slug: string
      source: TerminalGraphSelectionSource
    }
  | { type: "node-release"; slug: string; movementPx: number }
  | { type: "hover-start"; slug: string }
  | { type: "hover-end"; slug?: string }
  | { type: "drag-start"; pointerId: number; x: number; y: number }
  | { type: "drag-move"; pointerId: number; x: number; y: number }
  | { type: "drag-end"; pointerId: number }
  | { type: "drag-cancel"; pointerId?: number }
  | { type: "inertia-settled" }
  | { type: "panel-settled" }
  | { type: "panel-close" }
  | { type: "blank-canvas" }
  | { type: "escape" }
  | { type: "set-color-mode"; mode: TerminalGraphColorMode }
  | { type: "toggle-color-mode" }
  | { type: "set-sound-enabled"; isEnabled: boolean }
  | { type: "toggle-sound" }
  | { type: "set-reduced-motion"; isEnabled: boolean }
  | { type: "fail"; kind: TerminalGraphFailureKind; message: string }
  | { type: "retry" }
  | { type: "reset" }
  | { type: "focus-restored" }
  | { type: "hydrate-route"; route: TerminalGraphRouteState }

type TerminalGraphRouteIntentKind =
  | "noop"
  | "query"
  | "selection"
  | "dismiss-selection"

export type TerminalGraphRouteIntent = {
  kind: TerminalGraphRouteIntentKind
  history: "none" | "replace" | "push"
  /** TanStack Router's `replace` flag, made explicit for the route adapter. */
  shouldReplace: boolean
  /** Graph-only route changes never reset the document scroll position. */
  shouldResetScroll: false
  search: TerminalGraphRouteState
}

function normalizeWhitespace(value: string): string {
  return value.normalize("NFKC").replace(/\s+/g, " ").trim()
}

export function normalizeTerminalGraphQuery(
  value: unknown
): string | undefined {
  if (typeof value !== "string") return undefined
  const normalized = normalizeWhitespace(value)
  if (!normalized) return undefined
  return normalized.slice(0, TERMINAL_GRAPH_QUERY_MAX_LENGTH)
}

export function normalizeTerminalGraphTerm(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const normalized = value.trim().toLocaleLowerCase("en-US")
  return TERMINAL_GRAPH_SLUG_PATTERN.test(normalized) ? normalized : undefined
}

function normalizedSlugSet(
  validSlugs?: ReadonlySet<string>
): Set<string> | undefined {
  if (!validSlugs) return undefined
  return new Set(
    [...validSlugs]
      .map((slug) => normalizeTerminalGraphTerm(slug))
      .filter((slug): slug is string => slug !== undefined)
  )
}

function resolveSlug(
  value: unknown,
  validSlugs?: ReadonlySet<string>
): string | null {
  const slug = normalizeTerminalGraphTerm(value)
  if (!slug) return null
  const known = normalizedSlugSet(validSlugs)
  return known && !known.has(slug) ? null : slug
}

function routeStateFromUnknown(
  input: unknown,
  validSlugs?: ReadonlySet<string>
): TerminalGraphRouteState {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return {}
  }

  const record = input as Record<string, unknown>
  const q = normalizeTerminalGraphQuery(record.q)
  const term = resolveSlug(record.term, validSlugs)
  return {
    ...(q ? { q } : {}),
    ...(term ? { term } : {}),
  }
}

/** Normalize both route inputs and route snapshots before comparing them. */
export function normalizeTerminalGraphRouteState(
  input: unknown,
  validSlugs?: ReadonlySet<string>
): TerminalGraphRouteState {
  return routeStateFromUnknown(input, validSlugs)
}

function createRouteIntent(
  kind: TerminalGraphRouteIntentKind,
  history: TerminalGraphRouteIntent["history"],
  search: TerminalGraphRouteState
): TerminalGraphRouteIntent {
  return {
    kind,
    history,
    shouldReplace: history === "replace",
    shouldResetScroll: false,
    search,
  }
}

function noopRouteIntent(
  search: TerminalGraphRouteState
): TerminalGraphRouteIntent {
  return createRouteIntent("noop", "none", search)
}

/**
 * A committed term is a durable navigation event. The intent is idempotent so
 * a route hydration/render cycle cannot push the same selection twice.
 */
export function createCommittedSelectionIntent(
  current: TerminalGraphRouteState,
  slug: unknown,
  validSlugs?: ReadonlySet<string>
): TerminalGraphRouteIntent {
  const normalizedCurrent = normalizeTerminalGraphRouteState(current)
  const term = resolveSlug(slug, validSlugs)
  if (!term) return noopRouteIntent(normalizedCurrent)

  const next: TerminalGraphRouteState = { term }
  if (normalizedCurrent.term === term && !normalizedCurrent.q) {
    return noopRouteIntent(normalizedCurrent)
  }
  // Selecting the already-selected term only clears a transient q; it must not
  // create another browser-history entry.
  if (normalizedCurrent.term === term) {
    return createRouteIntent("query", "replace", next)
  }
  return createRouteIntent("selection", "push", next)
}

export function createDismissSelectionIntent(
  current: TerminalGraphRouteState
): TerminalGraphRouteIntent {
  const normalizedCurrent = normalizeTerminalGraphRouteState(current)
  if (!normalizedCurrent.term) return noopRouteIntent(normalizedCurrent)
  return createRouteIntent(
    "dismiss-selection",
    "push",
    normalizedCurrent.q ? { q: normalizedCurrent.q } : {}
  )
}

function createDragState(
  state: TerminalGraphState,
  action: Extract<TerminalGraphAction, { type: "drag-start" }>
): TerminalGraphDragState {
  return {
    isActive: true,
    pointerId: action.pointerId,
    startX: action.x,
    startY: action.y,
    lastX: action.x,
    lastY: action.y,
    velocityX: 0,
    velocityY: 0,
    hasMoved: false,
    returnPhase: state.selectedSlug ? "selected" : "idle",
  }
}

function resetDragState(
  drag: TerminalGraphDragState,
  isActive = false,
  shouldPreserveVelocity = false
): TerminalGraphDragState {
  return {
    ...drag,
    isActive,
    pointerId: isActive ? drag.pointerId : null,
    startX: isActive ? drag.startX : null,
    startY: isActive ? drag.startY : null,
    lastX: isActive ? drag.lastX : null,
    lastY: isActive ? drag.lastY : null,
    ...(isActive || shouldPreserveVelocity
      ? {}
      : { velocityX: 0, velocityY: 0 }),
  }
}

function interactivePhaseFor(state: TerminalGraphState): TerminalGraphPhase {
  if (state.failure) return "fallback"
  if (state.search.isOpen) return "searching"
  if (state.selectedSlug) return "selected"
  return "idle"
}

function isPreInteractivePhase(phase: TerminalGraphPhase): boolean {
  return phase === "reserved" || phase === "loading" || phase === "revealing"
}

function isGraphInteractionBlocked(phase: TerminalGraphPhase): boolean {
  return isPreInteractivePhase(phase) || phase === "fallback"
}

function preservePreInteractivePhase(
  current: TerminalGraphPhase,
  fallback: TerminalGraphPhase
): TerminalGraphPhase {
  return isPreInteractivePhase(current) ? current : fallback
}

function clearSelection(
  state: TerminalGraphState,
  focusRestore: TerminalGraphFocusTarget = "canvas"
): TerminalGraphState {
  const withoutSelection = {
    ...state,
    selectedSlug: null,
    panel: { status: "closed" as const },
  }
  return {
    ...state,
    phase: preservePreInteractivePhase(
      state.phase,
      interactivePhaseFor(withoutSelection)
    ),
    selectedSlug: null,
    selectedSource: null,
    pendingSelectedSlug: null,
    hoveredSlug: null,
    panel: { status: "closed" },
    focusRestore,
    drag: resetDragState(state.drag),
  }
}

function selectTerm(
  state: TerminalGraphState,
  slug: unknown,
  source: TerminalGraphSelectionSource,
  options: TerminalGraphReducerOptions,
  shouldPreserveSearch = false
): TerminalGraphState {
  const resolved = resolveSlug(slug, options.validSlugs)
  if (!resolved) return state

  const search = shouldPreserveSearch
    ? state.search
    : {
        isOpen: false,
        query: "",
        resultSlugs: [],
        activeResultIndex: 0,
      }

  return {
    ...state,
    phase: interactivePhaseFor({ ...state, search, selectedSlug: resolved }),
    selectedSlug: resolved,
    selectedSource: source,
    pendingSelectedSlug: null,
    hoveredSlug: null,
    search,
    panel: { status: state.isReducedMotion ? "open" : "entering" },
    focusRestore: null,
    drag: resetDragState(state.drag),
  }
}

function applyPendingSelection(
  state: TerminalGraphState,
  options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (isPreInteractivePhase(state.phase)) return state
  if (!state.pendingSelectedSlug) {
    // A q-only deep link can keep search open while the scene is entering. Once
    // the lifecycle reaches an interactive frame, expose that overlay phase
    // rather than silently reporting idle with an open search surface.
    return state.search.isOpen && !state.failure
      ? { ...state, phase: "searching" }
      : state
  }
  return selectTerm(
    state,
    state.pendingSelectedSlug,
    "route",
    options,
    // Hydration captured both q and term. Preserve that exact combined route
    // snapshot when the pending selection is applied after reveal.
    state.search.isOpen
  )
}

function withSearchResults(
  state: TerminalGraphState,
  resultSlugs: readonly string[],
  options: TerminalGraphReducerOptions
): TerminalGraphState {
  const seen = new Set<string>()
  const resolved = resultSlugs
    .map((slug) => resolveSlug(slug, options.validSlugs))
    .filter((slug): slug is string => {
      if (!slug || seen.has(slug)) return false
      seen.add(slug)
      return true
    })

  const activeResultIndex = Math.min(
    state.search.activeResultIndex,
    Math.max(0, resolved.length - 1)
  )
  return {
    ...state,
    search: {
      ...state.search,
      resultSlugs: resolved,
      activeResultIndex,
    },
  }
}

type TerminalGraphTransition = (
  state: TerminalGraphState,
  action: TerminalGraphAction,
  options: TerminalGraphReducerOptions
) => TerminalGraphState

function transitionLifecycle(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type === "section-visible") {
    return state.phase === "reserved" ? { ...state, phase: "loading" } : state
  }

  if (action.type === "engine-ready") {
    if (state.phase !== "loading") return state
    const next = {
      ...state,
      phase: state.isReducedMotion ? ("idle" as const) : ("revealing" as const),
    }
    return state.isReducedMotion ? applyPendingSelection(next, options) : next
  }

  if (action.type === "reveal-complete") {
    if (state.phase !== "revealing") return state
    return applyPendingSelection({ ...state, phase: "idle" }, options)
  }

  return state
}

function transitionSearch(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type === "open-search") {
    const query =
      normalizeTerminalGraphQuery(action.query ?? state.search.query) ?? ""
    const hasSearchChanged = query !== state.search.query
    return {
      ...state,
      phase: preservePreInteractivePhase(state.phase, "searching"),
      search: {
        ...state.search,
        isOpen: true,
        query,
        ...(hasSearchChanged || !query
          ? { resultSlugs: [], activeResultIndex: 0 }
          : {}),
      },
      focusRestore: "search",
    }
  }

  if (action.type === "close-search") {
    const shouldClearQuery = action.shouldClearQuery ?? true
    const search = shouldClearQuery
      ? {
          isOpen: false,
          query: "",
          resultSlugs: [],
          activeResultIndex: 0,
        }
      : { ...state.search, isOpen: false }
    return {
      ...state,
      phase: preservePreInteractivePhase(
        state.phase,
        state.selectedSlug ? "selected" : "idle"
      ),
      search,
      focusRestore: state.selectedSlug ? "selected-term" : "canvas",
    }
  }

  if (action.type === "set-query") {
    const query = normalizeTerminalGraphQuery(action.query) ?? ""
    const hasQueryChanged = query !== state.search.query
    const next: TerminalGraphState = {
      ...state,
      phase: preservePreInteractivePhase(state.phase, "searching"),
      search: {
        ...state.search,
        isOpen: true,
        query,
        ...(action.resultSlugs
          ? {
              resultSlugs: [...action.resultSlugs],
              ...(hasQueryChanged ? { activeResultIndex: 0 } : {}),
            }
          : { resultSlugs: [], activeResultIndex: 0 }),
      },
      focusRestore: null,
    }
    return action.resultSlugs
      ? withSearchResults(next, action.resultSlugs, options)
      : next
  }

  if (action.type === "clear-query") {
    return {
      ...state,
      phase: preservePreInteractivePhase(
        state.phase,
        interactivePhaseFor({ ...state, failure: null })
      ),
      search: {
        ...state.search,
        query: "",
        resultSlugs: [],
        activeResultIndex: 0,
      },
    }
  }

  if (action.type === "set-search-results") {
    return withSearchResults(state, action.resultSlugs, options)
  }

  if (action.type === "move-search-result") {
    const count = state.search.resultSlugs.length
    if (count === 0) return state
    const nextIndex =
      (((state.search.activeResultIndex + action.delta) % count) + count) %
      count
    return {
      ...state,
      search: { ...state.search, activeResultIndex: nextIndex },
    }
  }

  if (action.type === "commit-search") {
    const slug = state.search.resultSlugs[state.search.activeResultIndex]
    return slug ? selectTerm(state, slug, "search", options) : state
  }

  return state
}

function transitionSelection(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type === "select-term") {
    return selectTerm(state, action.slug, action.source, options)
  }

  if (action.type === "node-release") {
    return action.movementPx <= TERMINAL_GRAPH_DRAG_MOVEMENT_THRESHOLD_PX
      ? selectTerm(state, action.slug, "pointer", options)
      : state
  }

  if (action.type === "panel-close") {
    return clearSelection(state)
  }

  if (action.type === "blank-canvas") {
    return state.selectedSlug
      ? clearSelection(state)
      : { ...state, hoveredSlug: null }
  }

  return state
}

function transitionHover(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type === "hover-start") {
    const slug = resolveSlug(action.slug, options.validSlugs)
    if (!slug || isGraphInteractionBlocked(state.phase)) return state
    return {
      ...state,
      phase: "hovered",
      hoveredSlug: slug,
    }
  }

  if (action.type === "hover-end") {
    if (isGraphInteractionBlocked(state.phase)) return state
    if (action.slug && action.slug !== state.hoveredSlug) return state
    return {
      ...state,
      phase: interactivePhaseFor(state),
      hoveredSlug: null,
    }
  }

  return state
}

function transitionDrag(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  _options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type === "drag-start") {
    if (isGraphInteractionBlocked(state.phase)) return state
    return {
      ...state,
      phase: "dragging",
      hoveredSlug: null,
      drag: createDragState(state, action),
    }
  }

  if (action.type === "drag-move") {
    if (!state.drag.isActive || state.drag.pointerId !== action.pointerId)
      return state
    const dx = action.x - (state.drag.lastX ?? action.x)
    const dy = action.y - (state.drag.lastY ?? action.y)
    const totalX = action.x - (state.drag.startX ?? action.x)
    const totalY = action.y - (state.drag.startY ?? action.y)
    return {
      ...state,
      drag: {
        ...state.drag,
        lastX: action.x,
        lastY: action.y,
        velocityX: dx,
        velocityY: dy,
        hasMoved:
          state.drag.hasMoved ||
          Math.hypot(totalX, totalY) >
            TERMINAL_GRAPH_DRAG_MOVEMENT_THRESHOLD_PX,
      },
    }
  }

  if (action.type === "drag-end") {
    if (!state.drag.isActive || state.drag.pointerId !== action.pointerId)
      return state
    return {
      ...state,
      phase: state.drag.returnPhase,
      // Keep the final sample available to the engine as the seed for its
      // inertia loop. A later selection/cancel/reset clears it.
      drag: resetDragState(state.drag, false, true),
    }
  }

  if (action.type === "drag-cancel") {
    if (
      !state.drag.isActive ||
      (action.pointerId !== undefined &&
        state.drag.pointerId !== action.pointerId)
    ) {
      return state
    }
    return {
      ...state,
      phase: state.drag.returnPhase,
      drag: resetDragState(state.drag),
    }
  }

  if (action.type === "inertia-settled") {
    return {
      ...state,
      drag: {
        ...state.drag,
        velocityX: 0,
        velocityY: 0,
        hasMoved: false,
      },
    }
  }

  return state
}

function transitionPanel(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  _options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type !== "panel-settled") return state
  return state.panel.status === "entering"
    ? { ...state, panel: { status: "open" } }
    : state
}

function transitionEscape(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type !== "escape") return state
  if (state.drag.isActive) {
    return {
      ...state,
      phase: state.drag.returnPhase,
      drag: resetDragState(state.drag),
    }
  }
  if (state.search.query) {
    return transitionSearch(state, { type: "clear-query" }, options)
  }
  if (state.search.isOpen) {
    return transitionSearch(
      state,
      { type: "close-search", shouldClearQuery: true },
      options
    )
  }
  if (state.selectedSlug) return clearSelection(state)
  if (state.hoveredSlug) return { ...state, phase: "idle", hoveredSlug: null }
  return state
}

function transitionSettings(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  _options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type === "set-color-mode") {
    return { ...state, colorMode: action.mode }
  }

  if (action.type === "toggle-color-mode") {
    return {
      ...state,
      colorMode: state.colorMode === "monochrome" ? "spectrum" : "monochrome",
    }
  }

  if (action.type === "set-sound-enabled") {
    return { ...state, isSoundEnabled: action.isEnabled }
  }

  if (action.type === "toggle-sound") {
    return { ...state, isSoundEnabled: !state.isSoundEnabled }
  }

  if (action.type === "set-reduced-motion") {
    if (!action.isEnabled || state.isReducedMotion) {
      return { ...state, isReducedMotion: action.isEnabled }
    }
    const next = {
      ...state,
      isReducedMotion: true,
      ...(state.phase === "revealing" ? { phase: "idle" as const } : {}),
      panel:
        state.panel.status === "entering"
          ? { status: "open" as const }
          : state.panel,
    }
    return applyPendingSelection(next, _options)
  }

  return state
}

function transitionFailure(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  _options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type === "fail") {
    return {
      ...state,
      phase: "fallback",
      failure: { kind: action.kind, message: action.message },
      hoveredSlug: null,
      drag: resetDragState(state.drag),
      panel: state.selectedSlug ? { status: "open" } : { status: "closed" },
    }
  }

  if (action.type === "retry") {
    return state.failure
      ? {
          ...state,
          phase: "loading",
          failure: null,
          hoveredSlug: null,
          drag: resetDragState(state.drag),
          panel: state.selectedSlug ? state.panel : { status: "closed" },
        }
      : state
  }

  return state
}

function transitionReset(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  _options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type !== "reset") return state
  return {
    ...state,
    phase: state.failure ? "fallback" : "idle",
    selectedSlug: null,
    selectedSource: null,
    pendingSelectedSlug: null,
    hoveredSlug: null,
    search: {
      isOpen: false,
      query: "",
      resultSlugs: [],
      activeResultIndex: 0,
    },
    panel: { status: "closed" },
    drag: resetDragState(state.drag),
    focusRestore: "canvas",
  }
}

function transitionFocus(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  _options: TerminalGraphReducerOptions
): TerminalGraphState {
  return action.type === "focus-restored"
    ? { ...state, focusRestore: null }
    : state
}

function transitionRoute(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  options: TerminalGraphReducerOptions
): TerminalGraphState {
  if (action.type !== "hydrate-route") return state
  const route = normalizeTerminalGraphRouteState(
    action.route,
    options.validSlugs
  )
  const query = route.q ?? ""
  const search: TerminalGraphSearchState = {
    isOpen: Boolean(query),
    query,
    resultSlugs: [],
    activeResultIndex: 0,
  }
  const withoutSelection: TerminalGraphState = {
    ...state,
    selectedSlug: null,
    selectedSource: null,
    pendingSelectedSlug: route.term ?? null,
    hoveredSlug: null,
    search,
    panel: { status: "closed" },
    focusRestore: null,
  }
  // A route can arrive before the lazy engine does. Keep the lifecycle
  // phase authoritative while it is still reserved/loading/revealing;
  // otherwise a q value would strand the state in `searching` and prevent
  // the ready/reveal events from ever applying a pending direct term.
  const nextPhase = preservePreInteractivePhase(
    state.phase,
    route.q ? "searching" : interactivePhaseFor(withoutSelection)
  )
  const next = { ...withoutSelection, phase: nextPhase }
  if (!route.term || isPreInteractivePhase(state.phase)) return next
  return selectTerm(next, route.term, "route", options, Boolean(route.q))
}

const terminalGraphTransitionByAction: Record<
  TerminalGraphAction["type"],
  TerminalGraphTransition
> = {
  "section-visible": transitionLifecycle,
  "engine-ready": transitionLifecycle,
  "reveal-complete": transitionLifecycle,
  "open-search": transitionSearch,
  "close-search": transitionSearch,
  "set-query": transitionSearch,
  "clear-query": transitionSearch,
  "set-search-results": transitionSearch,
  "move-search-result": transitionSearch,
  "commit-search": transitionSearch,
  "select-term": transitionSelection,
  "node-release": transitionSelection,
  "hover-start": transitionHover,
  "hover-end": transitionHover,
  "drag-start": transitionDrag,
  "drag-move": transitionDrag,
  "drag-end": transitionDrag,
  "drag-cancel": transitionDrag,
  "inertia-settled": transitionDrag,
  "panel-settled": transitionPanel,
  "panel-close": transitionSelection,
  "blank-canvas": transitionSelection,
  escape: transitionEscape,
  "set-color-mode": transitionSettings,
  "toggle-color-mode": transitionSettings,
  "set-sound-enabled": transitionSettings,
  "toggle-sound": transitionSettings,
  "set-reduced-motion": transitionSettings,
  fail: transitionFailure,
  retry: transitionFailure,
  reset: transitionReset,
  "focus-restored": transitionFocus,
  "hydrate-route": transitionRoute,
}

export function createInitialTerminalGraphState(
  options: CreateTerminalGraphStateOptions = {}
): TerminalGraphState {
  return {
    phase: "reserved",
    selectedSlug: null,
    selectedSource: null,
    pendingSelectedSlug: null,
    hoveredSlug: null,
    drag: {
      isActive: false,
      pointerId: null,
      startX: null,
      startY: null,
      lastX: null,
      lastY: null,
      velocityX: 0,
      velocityY: 0,
      hasMoved: false,
      returnPhase: "idle",
    },
    search: {
      isOpen: false,
      query: "",
      resultSlugs: [],
      activeResultIndex: 0,
    },
    panel: { status: "closed" },
    colorMode: options.colorMode ?? TERMINAL_GRAPH_DEFAULT_COLOR_MODE,
    isSoundEnabled: options.isSoundEnabled ?? false,
    isReducedMotion: options.isReducedMotion ?? false,
    failure: null,
    focusRestore: null,
  }
}

export function reduceTerminalGraphState(
  state: TerminalGraphState,
  action: TerminalGraphAction,
  options: TerminalGraphReducerOptions = {}
): TerminalGraphState {
  return terminalGraphTransitionByAction[action.type](state, action, options)
}
